import { noop } from 'lodash-es';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { EM_DASH } from '~/common/constants/unicode';
import { User } from '~/components/Shared/User/User';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Header } from '~/eds/src/components/Header/Header';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { Table } from '~/eds/src/modules/Table/Table';
import { Highlight } from '~/features/document-viewer/types/highlight';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { FieldValue } from '../fields/types';
import { TableRowValue } from '../types/table';
import { PromptModelWorkflow } from './types';
import { mapWorkflowStateToDisplay } from './utils';

interface Props {
  isLoading: boolean;
  modelId: Uuid;
  onDocumentIdClick: ({
    documentVersionId,
    highlights,
    workflow,
  }: {
    documentVersionId: number;
    highlights: Highlight[];
    workflow: PromptModelWorkflow;
  }) => void;
}

export const DebugModelDocumentsInScope = ({
  isLoading,
  modelId,
  onDocumentIdClick,
}: Props) => {
  const dispatch = useDispatch();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const intl = useIntl();

  const oneHundred = formatNumber(100);

  const workflowPredictions = useSelector(
    slices.xRayDebug.selectors.selectWorkflowPredictions,
  );
  const cursor = useSelector(slices.xRayDebug.selectors.selectCursor);

  const [
    getWorkflowPredictions,
    {
      data,
      isLoading: isLoadingWorkflow,
      isFetching: isFetchingWorkflow,
      isError,
    },
  ] = api.endpoints.getPromptModelWorkflowPredictions.useLazyQuery();

  const handleUpdateWorkflowPredictions = useCallback(
    (workflowPredictionsData?: {
      cursor: Nullable<string>;
      items: PromptModelWorkflow[];
    }) => {
      dispatch(
        slices.xRayDebug.actions.setCursor(
          workflowPredictionsData?.cursor ?? null,
        ),
      );
      if (workflowPredictionsData?.items) {
        dispatch(
          slices.xRayDebug.actions.setWorkflowPredictions([
            ...workflowPredictions,
            ...workflowPredictionsData.items,
          ]),
        );
      }
    },
    [workflowPredictions],
  );

  const debouncedGetWorkflowPredictions = useDebounce(
    (params) =>
      getWorkflowPredictions(params, true)
        .unwrap()
        .then(handleUpdateWorkflowPredictions),
    1000,
    [data, handleUpdateWorkflowPredictions],
  );

  if (!data && !workflowPredictions.length) {
    debouncedGetWorkflowPredictions({ modelId, highlightMethod });
  }

  const columns = [
    {
      key: 'entity.id',
      cellType: 'link',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'CustomAiModelDebugDocsInScope.documentVersionId',
        defaultMessage: 'Document Version ID',
        description: 'Column header for the document version identifier',
      }),
      mapCellProps: (workflow: PromptModelWorkflow) => ({
        text: workflow.entity?.id ?? EM_DASH,
        onClick: workflow.entity?.id
          ? () => {
              const isTableModel =
                workflow.output?.value_type === 'table_value';
              onDocumentIdClick({
                documentVersionId: Number(workflow.entity!.id),
                highlights: isTableModel
                  ? ((workflow.output?.value as TableRowValue[])?.flatMap(
                      (row: TableRowValue) =>
                        row.cells.flatMap((cell) => cell.highlights ?? []),
                    ) ?? [])
                  : ((workflow.output as FieldValue)?.highlights ?? []),
                workflow,
              });
            }
          : noop,
      }),
    },
    {
      key: 'state',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'CustomAiModelDebugDocsInScope.status',
        defaultMessage: 'Status',
        description: 'Column header for model workflow status',
      }),
      disableSortBy: true,
      width: 'm',
      mapCellProps: (workflow: PromptModelWorkflow) => ({
        text: mapWorkflowStateToDisplay(workflow.state, intl),
      }),
    },
    {
      key: 'created_by_user_id',
      cellType: 'user',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'CustomAiModelDebugDocsInScope.createdBy',
        defaultMessage: 'Created By',
        description: 'Column header for who created the model workflow',
      }),
      mapCellProps: (workflow: PromptModelWorkflow) => ({
        asyncUser: {
          id: Number(workflow.created_by_user_id),
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'datetime_created',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CustomAiModelDebugDocsInScope.dateCreated',
        defaultMessage: 'Date Created',
        description: 'Column header for when the model workflow was created',
      }),
      mapCellProps: (workflow: PromptModelWorkflow) => ({
        datetime: workflow.datetime_created,
        format: 'short',
        tooltip: workflow.datetime_created
          ? formatDate(workflow.datetime_created, 'time')
          : undefined,
      }),
    },
    {
      key: 'datetime_updated',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CustomAiModelDebugDocsInScope.updatedDate',
        defaultMessage: 'Date Last Updated',
        description:
          'Column header for when the model workflow was last updated',
      }),
      mapCellProps: (workflow: PromptModelWorkflow) => ({
        datetime: workflow.datetime_updated,
        format: 'short',
        tooltip: workflow.datetime_updated
          ? formatDate(workflow.datetime_updated, 'time')
          : undefined,
      }),
    },
  ];

  if (isError) {
    return (
      <ContentContainer
        placeholderContent={{
          description: intl.formatMessage({
            id: 'CustomAiModelDebugDocsInScope.errorMessage',
            defaultMessage:
              'There was an error loading the model workflow details. Refresh the page to try again.',
            description: 'Error message when the workflow data fails to load',
          }),
        }}
      />
    );
  }

  return (
    <Layout key={modelId} direction="column">
      <Header
        title={intl.formatMessage({
          id: 'CustomAiModelDebugDocsInScope.documentWorkflowPredictions',
          defaultMessage: 'Document Workflow Predictions',
          description:
            'Title for the table showing the model workflow prediction documents',
        })}
        actions={
          cursor
            ? [
                {
                  isLoading: isFetchingWorkflow,
                  text: isFetchingWorkflow
                    ? intl.formatMessage(
                        {
                          id: 'CustomAiModelDebugDocsInScope.loadingMore',
                          defaultMessage: 'Loading {oneHundred} more…',
                          description:
                            'Button text while loading more results. Example: oneHundred = 100',
                        },
                        { oneHundred },
                      )
                    : intl.formatMessage(
                        {
                          id: 'CustomAiModelDebugDocsInScope.loadMore',
                          defaultMessage: 'Load {oneHundred} more',
                          description:
                            'Button text to load 100 more results. Example: oneHundred = 100',
                        },
                        { oneHundred },
                      ),
                  onClick: () =>
                    getWorkflowPredictions({
                      cursor,
                      modelId,
                      highlightMethod,
                    })
                      .unwrap()
                      .then(handleUpdateWorkflowPredictions),
                },
              ]
            : undefined
        }
      />
      <Table
        columns={columns}
        data={workflowPredictions}
        isLoading={isLoading || isLoadingWorkflow || isFetchingWorkflow}
        name="Document Workflow Predictions Table"
        options={{ enableSelectRows: false }}
      />
    </Layout>
  );
};
